import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { buildings, units, certificates, documents } from '../services/api';

const FORM_B_FIELDS = [
  { key: 'crf_balance', label: 'Contingency Reserve Fund balance', placeholder: 'e.g. $185,000' },
  { key: 'crf_planned_expenditures', label: 'Planned CRF expenditures', placeholder: 'e.g. Roof replacement, 2027' },
  { key: 'special_levies', label: 'Special levies', placeholder: 'e.g. None approved and unpaid' },
  { key: 'rental_restrictions', label: 'Rental restriction bylaw', placeholder: 'e.g. Rentals permitted, no restriction' },
  { key: 'pet_restrictions', label: 'Pet restriction bylaw', placeholder: 'e.g. Two pets max, no dogs over 25 lbs' },
  { key: 'age_restrictions', label: 'Age restrictions', placeholder: 'e.g. None' },
  { key: 'insurance_carrier', label: 'Insurance carrier', placeholder: 'e.g. Intact Insurance' },
  { key: 'insurance_policy_number', label: 'Policy number', placeholder: '' },
  { key: 'insurance_coverage', label: 'Coverage amount', placeholder: 'e.g. $12,000,000' },
  { key: 'insurance_deductible', label: 'Deductible', placeholder: 'e.g. $25,000 (water damage)' },
  { key: 'approved_alterations', label: 'Approved alterations to this unit', placeholder: 'e.g. None on file' },
  { key: 'legal_proceedings', label: 'Legal proceedings against the strata', placeholder: 'e.g. None' },
];

const FORM_F_FIELDS = [
  { key: 'payment_notes', label: 'Additional notes', placeholder: 'Anything else relevant to payment status' },
];

function Field({ label, placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

export default function CertificateGenerator() {
  const [buildingList, setBuildingList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [buildingId, setBuildingId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [formType, setFormType] = useState('form_b');
  const [preview, setPreview] = useState(null);
  const [fields, setFields] = useState({});
  const [saveDefaults, setSaveDefaults] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    buildings.list().then((res) => setBuildingList(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setUnitId('');
    setPreview(null);
    setResult(null);
    if (!buildingId) { setUnitList([]); return; }
    units.list({ building_id: buildingId }).then((res) => setUnitList(res.data)).catch(() => {});
  }, [buildingId]);

  useEffect(() => {
    setResult(null);
    if (!unitId) { setPreview(null); return; }
    setLoadingPreview(true);
    certificates.previewData(unitId)
      .then((res) => {
        setPreview(res.data);
        setFields(res.data.certificate_defaults || {});
      })
      .catch(() => toast.error('Failed to load unit data'))
      .finally(() => setLoadingPreview(false));
  }, [unitId]);

  const setField = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

  const handleGenerate = async () => {
    if (!unitId) { toast.error('Choose a unit first'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await certificates.generate({
        unit_id: unitId,
        form_type: formType,
        fields,
        save_as_defaults: saveDefaults,
      });
      setResult(res.data);
      toast.success(`${formType === 'form_b' ? 'Form B' : 'Form F'} generated`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const activeFields = formType === 'form_b' ? FORM_B_FIELDS : FORM_F_FIELDS;

  const handleDownloadResult = async () => {
    if (!result) return;
    try {
      const res = await documents.download(result.document.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.document.name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Certificate Generator</h1>
        <p className="text-gray-500 text-sm mt-1">Generate a Form B (Information Certificate) or Form F (Certificate of Payment).</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Building</label>
            <select value={buildingId} onChange={(e) => setBuildingId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select a building…</option>
              {buildingList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Unit</label>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!buildingId}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value="">Select a unit…</option>
              {unitList.map((u) => <option key={u.id} value={u.id}>Unit {u.unit_number}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Form Type</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'form_b', label: 'Form B', desc: 'Information Certificate' },
              { v: 'form_f', label: 'Form F', desc: 'Certificate of Payment' },
            ].map((t) => (
              <button key={t.v} type="button" onClick={() => setFormType(t.v)}
                className={`text-left p-3 rounded-lg border-2 transition-all text-sm ${
                  formType === t.v ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <span className="font-medium text-gray-800">{t.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {loadingPreview && (
          <div className="text-sm text-gray-400 py-4 text-center">Loading unit data…</div>
        )}

        {preview && !loadingPreview && (
          <>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Auto-filled from records</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <span className="text-gray-500">Strata plan</span><span className="text-gray-900">{preview.building.strata_plan || 'N/A'}</span>
                <span className="text-gray-500">Monthly fee</span><span className="text-gray-900">{preview.unit.monthly_fee ? `$${preview.unit.monthly_fee.toFixed(2)}` : 'N/A'}</span>
                <span className="text-gray-500">Amount owing</span>
                <span className={preview.arrears.total_owed > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                  ${preview.arrears.total_owed.toFixed(2)}
                </span>
                <span className="text-gray-500">Fee for this certificate</span>
                <span className="text-gray-900">${(formType === 'form_b' ? preview.fees.form_b_fee : preview.fees.form_f_fee).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fields not on file — please confirm</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {activeFields.map((f) => (
                  <Field key={f.key} label={f.label} placeholder={f.placeholder}
                    value={fields[f.key]} onChange={setField(f.key)} />
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={saveDefaults} onChange={(e) => setSaveDefaults(e.target.checked)}
                className="rounded border-gray-300" />
              Save these as defaults for this building (reused next time)
            </label>

            <div className="flex justify-end">
              <button onClick={handleGenerate} disabled={generating}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm disabled:opacity-60 transition-colors">
                {generating ? 'Generating…' : `Generate ${formType === 'form_b' ? 'Form B' : 'Form F'}`}
              </button>
            </div>
          </>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-4">
        This certificate is generated from data on file plus the information you confirm above. It is a
        convenience template, not a substitute for legal review — verify accuracy before relying on it in
        a real-estate transaction.
      </p>

      {result && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-green-800">{result.document.name}</h3>
            <p className="text-xs text-green-700 mt-0.5">Saved to the Document Vault.</p>
          </div>
          <button onClick={handleDownloadResult}
            className="text-sm bg-white border border-green-300 text-green-700 font-medium px-4 py-2 rounded-lg hover:bg-green-100">
            Download PDF
          </button>
        </div>
      )}
    </div>
  );
}
